// EXPERIMENT: native_share_title_experiment — remove with experiment cleanup
import { useCallback, useMemo } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';
import type {
	EXPERIMENT_NATIVE_SHARE_TITLE_EXPERIMENT,
	ResolvedExperimentData,
} from '@change-corgi/experiments/configs';

import { treatExperiment } from '#src/app/shared/api/experiments';
import { useTrackExperimentExposure } from '#src/app/shared/hooks/tracking';

import type { ShareConfig } from '../shared/types';

import { nativeShareTitleTreats } from './treats';

type NativeShareTitleExperiment = ResolvedExperimentData<typeof EXPERIMENT_NATIVE_SHARE_TITLE_EXPERIMENT>;

type Variation = NativeShareTitleExperiment['variation'];
type TitleInput = Readonly<{ petitionTitle: string; signLabel: string }>;

// Keyed on the config's own variations, so a renamed arm fails to compile. The OS truncates a title that
// doesn't fit the share sheet header, so nothing is trimmed here.
const TITLES: Readonly<Record<Variation, (input: TitleInput) => string | undefined>> = {
	control: () => undefined,
	fake_control: () => undefined,
	variant_1: ({ petitionTitle }) => petitionTitle,
	variant_2: ({ petitionTitle, signLabel }) => `${signLabel}: ${petitionTitle}`,
	variant_3: ({ signLabel }) => signLabel,
};

type Options = Readonly<{
	/** Resolved with `treat: false` in the surface's page query: the treat happens at the native share tap. */
	experiment: NativeShareTitleExperiment;
	/** The share `context` the surface tracks with. It takes part only when listed in the FCM's `surfaces`. */
	surface: NativeShareTitleExperiment['config']['surfaces'][number];
	petitionTitle: string;
}>;

/**
 * native_share_title_experiment (CHANGE-99262): the `mobileNativeShare` share config for one surface,
 * or `undefined` when the surface doesn't take part: the arm's `title`, the arm stamped on the share events,
 * and an `onShareTap` that fires `$exposure` and treats the session at the native share tap.
 */
export function useNativeShareTitle({ experiment, surface, petitionTitle }: Options): ShareConfig['mobileNativeShare'] {
	const utilityContext = useUtilityContext();
	const { translate } = useI18n();

	// `treatExperiment` bypasses the config's `enabledIf`, so the flag is read again here. `!fallback`
	// skips resolutions that are not a real assignment, which `useTrackExperimentExposure` never exposes.
	const isEligible =
		experiment.config.enabled &&
		experiment.config.surfaces.includes(surface) &&
		experiment.experimentName !== '__invalid_experiment__' &&
		!experiment.fallback;

	// An override forces the arm, but `treatExperiment` would register the session's real assignment.
	// `treated` comes back true once an earlier page load's tap enrolled the session (every query here uses
	// `treat: false`), and the module latch doesn't survive a reload, so this keeps it to one enrollment.
	const treatsAtTap = isEligible && !experiment.override && !experiment.treated;

	// The resolved object stays `treated: false` after `treatExperiment`, and the exposure hook does nothing
	// while `!treated`, so it is armed here. It fires before the treat resolves: a treat that then fails
	// leaves an exposed but untreated session (rare, spread across arms; Redash reads the treat log).
	const experimentForExposure = useMemo(() => ({ ...experiment, treated: true }), [experiment]);
	const trackExposure = useTrackExperimentExposure(experimentForExposure);

	const onShareTap = useCallback(() => {
		const { experimentName } = experiment;
		if (!treatsAtTap || nativeShareTitleTreats.has(experimentName)) return;
		nativeShareTitleTreats.add(experimentName);

		// Unlike the treat-then-expose pattern elsewhere, `$exposure` is queued first, in the tap's tick:
		// nothing can be awaited before `navigator.share` on iOS, and the user leaves for another app (or
		// PSF redirects) before a treat round trip returns, so an exposure chained after it can arrive
		// late or not at all. Events carry no client timestamp, so this also keeps it ahead of the share
		// events. The exposure is deduped per mount, so a retry from another share button can send it again.
		void (async () => {
			try {
				await trackExposure();
			} catch (error) {
				// Reported, not rethrown: the share must not surface an analytics failure.
				void utilityContext.errorReporter.report(
					{
						error: new Error('Native share title experiment $exposure failed', { cause: error }),
						context: { feature: 'native_share_title_experiment' },
					},
					{ severity: 'warning' },
				);
			}
		})();
		void (async () => {
			if (!(await treatExperiment(utilityContext, experimentName))) nativeShareTitleTreats.delete(experimentName);
		})();
	}, [treatsAtTap, experiment, utilityContext, trackExposure]);

	const signLabel = translate('corgi.petition_gamma.petition_sign.sign_title');
	const { experimentName, variation, override } = experiment;

	return useMemo(() => {
		if (!isEligible) return undefined;
		return {
			onShareTap,
			title: TITLES[variation]({ petitionTitle, signLabel }),
			// An override shows the forced arm's title for QA, but its events stay out of the arm's numbers.
			trackingData: override
				? undefined
				: { native_share_title_experiment: experimentName, native_share_title_variation: variation },
		};
	}, [isEligible, onShareTap, variation, petitionTitle, signLabel, override, experimentName]);
}
